import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, Send, Sparkles, ShieldAlert, FileText } from "lucide-react";

const SUGGESTED = [
  "How should I store this material?",
  "What PPE should I wear when handling it?",
  "What's the typical shelf life?",
  "How do I clean up a spill?",
  "Is it compatible with EVA / silicone?",
];

/**
 * AI assistant dialog. `item` (optional) seeds the context with stock item info.
 */
const AIAssistantDialog = ({ open, item, onClose }) => {
  const [history, setHistory] = useState([]); // [{role, text}]
  const [busy, setBusy] = useState(false);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (open) {
      setHistory([]);
      setInput("");
      setSessionId(null);
    }
  }, [open, item?.id]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [history, busy]);

  if (!open) return null;

  const ask = async (text) => {
    const q = (text || input).trim();
    if (!q) return;
    setHistory((h) => [...h, { role: "user", text: q }]);
    setInput("");
    setBusy(true);
    try {
      const { data } = await api.post("/ai/material-info", {
        question: q,
        session_id: sessionId,
        context: item
          ? {
              name: item.name,
              type: item.type,
              department: item.department,
              is_hazardous: item.is_hazardous,
              pack_size: item.pack_size,
              supplier_name: item.supplier_default,
              msds_url: item.msds_url,
            }
          : null,
      });
      setSessionId(data.session_id);
      setHistory((h) => [...h, { role: "ai", text: data.answer }]);
    } catch (e) {
      toast.error("AI is briefly unavailable — try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[55] overflow-y-auto"
      data-testid="ai-overlay"
      onClick={onClose}
    >
      <div className="min-h-full flex items-start sm:items-center justify-center p-4">
        <div
          className="nb-card p-5 w-full max-w-2xl my-6 flex flex-col"
          style={{ maxHeight: "85vh" }}
          onClick={(e) => e.stopPropagation()}
          data-testid="ai-dialog"
        >
          <div className="flex items-start justify-between mb-3 gap-3">
            <div className="flex-1 min-w-0">
              <div className="label-eyebrow flex items-center gap-1.5">
                <Sparkles size={12} strokeWidth={2.5} className="text-[#7A4DBA]" />
                Material Assistant
              </div>
              <h3 className="font-display text-2xl mt-1 truncate">
                {item ? `Ask about ${item.name}` : "Ask about any material"}
              </h3>
              {item && (
                <div className="flex flex-wrap gap-2 mt-1 text-xs text-[#607087]">
                  {item.type && <span>{item.type}</span>}
                  {item.department && <span>· {item.department}</span>}
                  {item.pack_size && <span>· {item.pack_size}</span>}
                  {item.is_hazardous && (
                    <span className="flex items-center gap-1 text-[#E5263F] font-semibold">
                      <ShieldAlert size={12} strokeWidth={2.5} /> Hazardous
                    </span>
                  )}
                  {item.msds_url && (
                    <span className="flex items-center gap-1 text-[#228EBF]">
                      <FileText size={12} strokeWidth={2.5} /> MSDS on file
                    </span>
                  )}
                </div>
              )}
            </div>
            <button
              data-testid="ai-close"
              onClick={onClose}
              className="nb-btn bg-white p-2"
              aria-label="close"
            >
              <X size={18} strokeWidth={2.5} />
            </button>
          </div>

          <div
            ref={scrollRef}
            data-testid="ai-thread"
            className="flex-1 overflow-y-auto scrollbar-clean nb-card-sm p-3 mb-3 bg-white/55 min-h-[200px]"
          >
            {history.length === 0 && !busy && (
              <div className="text-sm text-[#607087]">
                Pick a question to get started, or type your own below.
              </div>
            )}
            {history.map((m, i) => (
              <div
                key={`${m.role}-${i}`}
                data-testid={`ai-msg-${m.role}-${i}`}
                className={`mb-3 flex ${m.role === "user" ? "justify-end" : "justify-start"}`}
              >
                <div
                  className={`px-3 py-2 rounded-2xl max-w-[85%] whitespace-pre-wrap text-sm leading-relaxed ${
                    m.role === "user"
                      ? "bg-[#228EBF] text-white rounded-br-sm"
                      : "bg-white text-[#001740] rounded-bl-sm border border-[#001740]/10"
                  }`}
                >
                  {m.text}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start mb-3" data-testid="ai-thinking">
                <div className="px-3 py-2 rounded-2xl bg-white border border-[#001740]/10 text-sm text-[#607087] italic">
                  Thinking…
                </div>
              </div>
            )}
          </div>

          {history.length === 0 && (
            <div className="flex flex-wrap gap-2 mb-3">
              {SUGGESTED.map((s, i) => (
                <button
                  key={s}
                  data-testid={`ai-suggest-${i}`}
                  onClick={() => ask(s)}
                  disabled={busy}
                  className="nb-btn bg-white text-xs py-1.5 px-2.5"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <div className="flex items-center gap-2">
            <input
              data-testid="ai-input"
              className="nb-input flex-1"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && !busy && ask()}
              placeholder="Ask about safety, storage, compatibility…"
              disabled={busy}
            />
            <button
              data-testid="ai-send"
              onClick={() => ask()}
              disabled={busy || !input.trim()}
              className="nb-btn nb-btn-primary px-4 py-2.5 flex items-center gap-1.5"
            >
              <Send size={16} strokeWidth={2.5} /> Send
            </button>
          </div>
          <div className="text-[10px] text-[#607087] mt-2 italic">
            AI answers are guidance only — always check the official MSDS for safety-critical info.
          </div>
        </div>
      </div>
    </div>
  );
};

export default AIAssistantDialog;
